import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaInfoCircle, FaPaperPlane } from "react-icons/fa";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import MultiSelect from "../../components/ui/MultiSelect";
import { notificationsService } from "../../services/notifications.service";
import { usersService } from "../../services/users.service";
import { getErrorMsg } from "../../utils/error";

type Option = { value: string; label: string };

// MultiSelect is the only searchable picker; keep just the latest pick.
const single = (onPick: (v: string) => void) => (v: string[]) =>
  onPick(v[v.length - 1] ?? "");

const SavedSearchTestPush = () => {
  const { t } = useTranslation("common");
  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const [listingOptions, setListingOptions] = useState<Option[]>([]);
  const [fetching, setFetching] = useState(true);
  const [userId, setUserId] = useState("");
  const [listingId, setListingId] = useState("");
  const [searchName, setSearchName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([
      usersService.list({ take: 1000 }),
      notificationsService.listTestSavedSearchListings(),
    ])
      .then(([users, listings]) => {
        setUserOptions(
          users.data.map((u) => ({
            value: u.id,
            label: `${u.fullName || u.email} (${u.email})`,
          })),
        );
        setListingOptions(
          listings.map((l) => ({
            value: l.id,
            label: `${l.title || l.id} (${l.formType})`,
          })),
        );
      })
      .catch((err) => setError(getErrorMsg(err, t("test_push_failed"))))
      .finally(() => setFetching(false));
  }, [t]);

  const onSend = async () => {
    setError("");
    setSuccess("");
    if (!userId || !listingId) {
      setError(t("test_push_required"));
      return;
    }
    setLoading(true);
    try {
      await notificationsService.sendTestSavedSearch({
        userId,
        listingId,
        searchName: searchName.trim() || undefined,
      });
      setSuccess(t("test_push_sent"));
    } catch (err) {
      setError(getErrorMsg(err, t("test_push_failed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title={t("test_push")}>
      <div className="max-w-4xl mx-auto space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-4 rounded-lg flex items-center">
            <FaInfoCircle className="mr-2" />
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 p-4 rounded-lg flex items-center">
            <FaInfoCircle className="mr-2" />
            {success}
          </div>
        )}

        <Card className="p-6 space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("test_push_intro")}
          </p>
          <MultiSelect
            label={t("test_push_user")}
            options={userOptions}
            value={userId ? [userId] : []}
            onChange={single(setUserId)}
            placeholder={
              fetching ? t("loading") : t("test_push_user_placeholder")
            }
            disabled={fetching}
          />
          <MultiSelect
            label={t("test_push_listing")}
            options={listingOptions}
            value={listingId ? [listingId] : []}
            onChange={single(setListingId)}
            placeholder={
              fetching ? t("loading") : t("test_push_listing_placeholder")
            }
            disabled={fetching}
          />
          <Input
            label={t("test_push_search_name")}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            placeholder={t("test_push_search_name_placeholder")}
            maxLength={100}
          />
        </Card>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={onSend}
            isLoading={loading}
            className="px-8 flex items-center"
          >
            <FaPaperPlane className="mr-2" />
            <span>{t("test_push_send")}</span>
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default SavedSearchTestPush;
